'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const ProfileHoverCardContext = createContext(null);
/**
 * One media query for the whole app, read after mount so server markup and the first client
 * render never change, and kept live so an attached mouse turns the cards on. Older Safari's
 * MediaQueryList has only addListener/removeListener.
 */
function useFinePointer() {
    const [fine, setFine] = useState(false);
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return;
        }
        const query = window.matchMedia(FINE_POINTER);
        const update = () => {
            setFine(query.matches);
        };
        update();
        if (typeof query.addEventListener === 'function') {
            query.addEventListener('change', update);
            return () => {
                query.removeEventListener('change', update);
            };
        }
        query.addListener(update);
        return () => {
            query.removeListener(update);
        };
    }, []);
    return fine;
}
/** Mount once per app. Without it every ProfileHoverCard renders its child unwrapped. */
export function ProfileHoverCardProvider({ adapter, children, }) {
    const fine = useFinePointer();
    // The pointer is latched: once fine, it stays fine for the provider's lifetime, so a
    // fine-to-coarse flip never unmounts an open card or its actions host. The flag is not
    // latched — turning it off is the kill switch.
    const [finePointer, setFinePointer] = useState(false);
    if (fine && !finePointer) {
        setFinePointer(true);
    }
    const active = adapter.enabled && finePointer;
    const value = useMemo(() => ({ adapter, active }), [adapter, active]);
    return _jsx(ProfileHoverCardContext.Provider, { value: value, children: children });
}
export function useProfileHoverCard() {
    return useContext(ProfileHoverCardContext);
}
//# sourceMappingURL=ProfileHoverCardProvider.js.map